import React from 'react';
import { Stack } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import TextareaInput from '../../form/base/TextareaInput';
import TextInput from '../../form/base/TextInput';
import { important } from '../../../utils/global/helper';
import SignatureInputControlled from '../../form/base/SignatureInputControlled';
import type OptionsList from '../OptionsList';
import { WaiverOption } from '../../../types/waivers';
import WaiverImageDropzone from '../WaiverImageDropzone';

type ListProps = React.ComponentProps<typeof OptionsList>;

export default function TextSignatureOption({
  disabled,
  item,
  onChange,
  options,
}: {
  disabled: ListProps['disabled'];
  item: WaiverOption;
  onChange: ListProps['onChange'];
  options: ListProps['options'];
}) {
  const isImageType =
    item.type === 'SINGLE_IMAGE' || item.type === 'MULTIPLE_IMAGE';

  const sigCanvas = React.useRef<SignatureCanvas>(null);

  const Input =
    item.type === 'TEXT'
      ? TextInput
      : item.type === 'MULTILINE_TEXT'
      ? TextareaInput
      : SignatureInputControlled;

  return (
    <Stack
      my={0.5}
      boxSizing="border-box"
      width="100%"
      spacing={1}
      padding="16.5px 14px"
      borderRadius="10px"
      sx={theme => ({
        outlineWidth: '0px',
        outlineStyle: 'solid',
        outlineColor: theme.palette.darkGrey.main,
        ':hover': {
          outlineWidth: '0.5px',
        },
        ':focus-within': {
          outlineWidth: '2px',
          outlineColor: theme.palette.primary.main,
        },
      })}>
      <TextareaInput
        autoHeight
        name={`text-${item.id}`}
        controlled={{
          value: item.text,
          onChange: value => {
            const newOptions = options.map(o =>
              o.id === item.id ? { ...o, text: value } : o,
            );
            onChange(newOptions);
          },
        }}
        noBorder
        noDisabledBgColor
        placeholder="Enter text here"
        sx={theme => ({
          padding: 0,
          fontSize: important(theme.typography.h3.fontSize),
          margin: 0,
        })}
        disabled={disabled}
      />
      {isImageType ? (
        <WaiverImageDropzone
          multiple={item.type === 'MULTIPLE_IMAGE'}
          disabled
        />
      ) : (
        <Input name={item.id} disabled alwaysShowClear sigCanvas={sigCanvas} />
      )}
    </Stack>
  );
}
